import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X } from 'lucide-react';
import { cx } from './cx';
import { Button } from './Button';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Dialog shell: blurred scrim (wpFade), panel rising in (wpRise), focus kept
 * inside while open and returned to the opener on close. Esc and a scrim click
 * close it unless `dismissible` is false.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  width = 500,
  kind = 'content',
  showClose = true,
  dismissible = true,
  align = 'center',
  className,
  'aria-label': ariaLabel,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  width?: number;
  kind?: 'content' | 'confirm';
  showClose?: boolean;
  dismissible?: boolean;
  /** 'top' pins the panel near the top (long forms such as the upload modal). */
  align?: 'center' | 'top';
  className?: string;
  'aria-label'?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = `mdl-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first =
      panel?.querySelector<HTMLElement>('[data-autofocus]') ??
      Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).find((el) => !el.dataset.modalClose);
    (first ?? panel)?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && dismissible) {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    if (!items.length) return;
    const firstEl = items[0];
    const lastEl = items[items.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  };

  return createPortal(
    <div
      className={cx(
        'wp-fade fixed inset-0 flex justify-center overflow-auto p-5',
        align === 'top' ? 'items-start pt-10' : 'items-center',
        kind === 'confirm' ? 'z-[70]' : 'z-[60]',
      )}
      style={{
        background: kind === 'confirm' ? 'rgba(9,9,11,.5)' : 'rgba(9,9,11,.45)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panelRef}
        role={kind === 'confirm' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={cx(
          'w-full bg-modal border border-line2 rounded-[24px] shadow-wp outline-none my-auto',
          kind === 'confirm' ? 'p-[26px]' : 'p-[26px]',
          className,
        )}
        style={{
          maxWidth: width,
          animation: `wpRise ${kind === 'confirm' ? '.3s' : '.35s'} var(--ease) both`,
        }}
      >
        {(title || showClose) && kind === 'content' && (
          <div className="flex justify-between items-start gap-3 mb-4">
            <div className="min-w-0">
              {title && (
                <h2 id={titleId} className="text-[22px] font-semibold tracking-[-0.02em] leading-tight">
                  {title}
                </h2>
              )}
              {subtitle && <div className="text-[13.5px] text-t4 mt-1 leading-normal">{subtitle}</div>}
            </div>
            {showClose && (
              <button
                type="button"
                data-modal-close="true"
                onClick={onClose}
                aria-label="Close"
                className="w-8 h-8 rounded-full bg-fill2 text-t3 hover:text-t1 flex items-center justify-center shrink-0"
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {kind === 'confirm' && title && (
          <h2 id={titleId} className="sr-only">
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Two-button footer used by every form modal. */
export function ModalActions({
  onCancel,
  cancelLabel = 'Cancel',
  confirmLabel,
  confirmType = 'submit',
  onConfirm,
  busy,
  disabled,
  leading,
}: {
  onCancel: () => void;
  cancelLabel?: string;
  confirmLabel: string;
  confirmType?: 'submit' | 'button';
  onConfirm?: () => void;
  busy?: boolean;
  disabled?: boolean;
  leading?: ReactNode;
}) {
  return (
    <div className="flex gap-2.5 mt-1.5">
      {leading}
      <Button variant="ghost" shape="form" size="lg" onClick={onCancel}>
        {cancelLabel}
      </Button>
      <Button type={confirmType} shape="form" size="lg" className="flex-1" loading={busy} disabled={disabled} onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </div>
  );
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** 'danger' and 'default' both use the accent confirm; kept for API compatibility. */
  type?: 'default' | 'danger' | 'success';
  busy?: boolean;
  /** Icon tile; defaults to a trash can for danger dialogs and none otherwise. */
  icon?: ReactNode | null;
  /** When false the dialog stays open after confirming (caller closes it). */
  closeOnConfirm?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'default',
  busy,
  icon,
  closeOnConfirm = true,
}: ConfirmDialogProps) {
  const tile = icon === undefined ? (type === 'danger' ? <Trash2 size={20} strokeWidth={2} /> : null) : icon;
  return (
    <Modal open={isOpen} onClose={onClose} kind="confirm" title={title} width={420} showClose={false} dismissible={!busy}>
      {tile && (
        <div className="w-11 h-11 rounded-[13px] bg-acc-bg2 text-acc flex items-center justify-center mb-4" aria-hidden="true">
          {tile}
        </div>
      )}
      <div className="text-[20px] font-semibold tracking-[-0.01em]" aria-hidden="true">
        {title}
      </div>
      <div className="text-[14px] text-t3 mt-2 leading-[1.55]">{message}</div>
      <div className="flex gap-2.5 mt-[22px]">
        <Button variant="ghost" shape="form" size="lg" className="flex-1 font-normal" onClick={onClose} disabled={busy}>
          {cancelText}
        </Button>
        <Button
          shape="form"
          size="lg"
          className="flex-1"
          loading={busy}
          onClick={async () => {
            await onConfirm();
            if (closeOnConfirm) onClose();
          }}
        >
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
}
